import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '../../../../../../ui/canon/Button';
import Card from '../../../../../../ui/canon/Card';

// Question options based on stylesheet name
const questionOptions = (stylesheetName, intl) => {
  if (stylesheetName === 'rjpe') {
    return [
      intl.formatMessage({ id: 'dr_wizard.knockout_23' }),
      intl.formatMessage({ id: 'dr_wizard.knockout_24' }),
      intl.formatMessage({ id: 'dr_wizard.knockout_25' }),
      intl.formatMessage({ id: 'dr_wizard.knockout_26' }),
      intl.formatMessage({ id: 'dr_wizard.knockout_27' }),
      intl.formatMessage({ id: 'dr_wizard.knockout_28' }),
      intl.formatMessage({ id: 'dr_wizard.knockout_29' }),
      intl.formatMessage({ id: 'dr_wizard.knockout_30' }),
      intl.formatMessage({ id: 'dr_wizard.knockout_31' }),
      intl.formatMessage({ id: 'dr_wizard.knockout_32' }),
      intl.formatMessage({ id: 'dr_wizard.knockout_33' }),
      intl.formatMessage({ id: 'dr_wizard.knockout_34' }),
      intl.formatMessage({ id: 'dr_wizard.knockout_35' }),
      intl.formatMessage({ id: 'dr_wizard.knockout_36' }),
      intl.formatMessage({ id: 'dr_wizard.knockout_37' }),
      intl.formatMessage({ id: 'dr_wizard.knockout_38' }),
      intl.formatMessage({ id: 'dr_wizard.knockout_39' }),
      intl.formatMessage({ id: 'dr_wizard.knockout_40' }),
      intl.formatMessage({ id: 'dr_wizard.knockout_41' }),
      intl.formatMessage({ id: 'dr_wizard.knockout_42' }),
      intl.formatMessage({ id: 'dr_wizard.knockout_43' }),
      intl.formatMessage({ id: 'dr_wizard.knockout_44' }),
    ];
  }
  return [
    intl.formatMessage({ id: 'dr_wizard.knockout_1' }),
    intl.formatMessage({ id: 'dr_wizard.knockout_2' }),
    intl.formatMessage({ id: 'dr_wizard.knockout_3' }),
    intl.formatMessage({ id: 'dr_wizard.knockout_4' }),
    intl.formatMessage({ id: 'dr_wizard.knockout_5' }),
    intl.formatMessage({ id: 'dr_wizard.knockout_6' }),
    intl.formatMessage({ id: 'dr_wizard.knockout_7' }),
    intl.formatMessage({ id: 'dr_wizard.knockout_8' }),
    intl.formatMessage({ id: 'dr_wizard.knockout_9' }),
    intl.formatMessage({ id: 'dr_wizard.knockout_10' }),
    intl.formatMessage({ id: 'dr_wizard.knockout_11' }),
    intl.formatMessage({ id: 'dr_wizard.knockout_12' }),
    intl.formatMessage({ id: 'dr_wizard.knockout_13' }),
    intl.formatMessage({ id: 'dr_wizard.knockout_14' }),
    intl.formatMessage({ id: 'dr_wizard.knockout_15' }),
    intl.formatMessage({ id: 'dr_wizard.knockout_16' }),
    intl.formatMessage({ id: 'dr_wizard.knockout_17' }),
    intl.formatMessage({ id: 'dr_wizard.knockout_18' }),
    intl.formatMessage({ id: 'dr_wizard.knockout_19' }),
    intl.formatMessage({ id: 'dr_wizard.knockout_20' }),
    intl.formatMessage({ id: 'dr_wizard.knockout_21' }),
    intl.formatMessage({ id: 'dr_wizard.knockout_22' }),
  ];
};

// Move QuestionInput component outside to prevent re-creation on every render
const QuestionInput = React.memo(({ questionId, index, value, onDelete, onChange, questionWord, stylesheetName }) => {
  const [inputType, setInputType] = useState('create'); // 'create' or 'select'
  const intl = useIntl();

  return (
    <Card variant="subtle" size="md" className="space-y-2">
      <div className="flex justify-between items-start">
        <label htmlFor={questionId} className="block text-sm font-medium text-succession-text-primary">
          <FormattedMessage id="succession_deals.wizard.steps.deal_questions.question_label" values={{ index }} />
        </label>
        {index > 1 && (
          <Button
            type="button"
            onClick={() => onDelete(index)}
            variant="ghost"
            size="sm"
            className="text-red-600 hover:text-red-800"
          >
            <FormattedMessage id="succession_deals.wizard.steps.deal_questions.delete_button" />
          </Button>
        )}
      </div>

      {/* Question Type Selection */}
      <div className="flex space-x-4 mb-4">
        <label className="inline-flex items-center">
          <input
            type="radio"
            className="form-radio"
            name={`question-type-${questionId}`}
            checked={inputType === 'select'}
            onChange={() => setInputType('select')}
          />
          <span className="ml-2 text-sm text-succession-text-primary">
            <FormattedMessage id="dr_wizard.select_from_suggested" />
          </span>
        </label>
        <label className="inline-flex items-center">
          <input
            type="radio"
            className="form-radio"
            name={`question-type-${questionId}`}
            checked={inputType === 'create'}
            onChange={() => setInputType('create')}
          />
          <span className="ml-2 text-sm text-succession-text-primary">
            <FormattedMessage id="dr_wizard.create_my_own" />
          </span>
        </label>
      </div>

      {/* Question Input Area */}
      {inputType === 'select' ? (
        <select
          id={questionId}
          value={value || ''}
          onChange={(e) => onChange(questionId, e.target.value)}
          className="block w-full px-3 py-2 border border-succession-border rounded-md shadow-sm focus:ring-2 focus:ring-succession-focus-ring bg-succession-bg-primary text-succession-text-primary"
        >
          <option value="" disabled>
            {intl.formatMessage({ id: 'dr_wizard.please_select_a_suggested' })}
          </option>
          {questionOptions(stylesheetName, intl).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : (
        <textarea
          id={questionId}
          rows={3}
          value={value || ''}
          onChange={(e) => onChange(questionId, e.target.value)}
          className="block w-full px-3 py-2 border border-succession-border rounded-md shadow-sm focus:ring-2 focus:ring-succession-focus-ring resize-none bg-succession-bg-primary text-succession-text-primary placeholder:text-succession-text-muted"
          placeholder={intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_questions.question_placeholder' }, { questionWord })}
          maxLength={500}
        />
      )}

      <div className="flex justify-between text-xs text-succession-text-muted">
        <span><FormattedMessage id="succession_deals.wizard.steps.deal_questions.question_help_text" /></span>
        <span>{value?.length || 0}/500</span>
      </div>
    </Card>
  );
});

QuestionInput.propTypes = {
  questionId: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  value: PropTypes.string.isRequired,
  onDelete: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  questionWord: PropTypes.string.isRequired,
  stylesheetName: PropTypes.string.isRequired
};

const DealQuestions = ({ formData, updateFormData, errors, company }) => {
  const intl = useIntl();
  
  // Initialize from formData
  const getInitialQuestions = () => ({
    question_1: formData.question_1 || '',
    question_2: formData.question_2 || '',
    question_3: formData.question_3 || '',
    question_4: formData.question_4 || '',
    question_5: formData.question_5 || ''
  });

  const [questions, setQuestions] = useState(getInitialQuestions);

  const [numberOfQuestions, setNumberOfQuestions] = useState(() => {
    const initialQuestions = getInitialQuestions();
    const existingQuestions = Object.values(initialQuestions).filter(q => q.trim() !== '').length;
    return Math.max(existingQuestions, 1) + 1;
  });

  // Note: Removed sync useEffect to prevent infinite loops - initial state is set correctly from formData

  // Validation logic for questions
  const validateQuestions = useCallback(() => {
    const filledQuestions = Object.values(questions).filter(q => q && q.trim() !== '');
    const hasAtLeastOneQuestion = filledQuestions.length >= 1;
    
    // Check if all added questions have content
    let allAddedQuestionsHaveContent = true;
    for (let i = 1; i < numberOfQuestions; i++) {
      const questionKey = `question_${i}`;
      if (!questions[questionKey] || questions[questionKey].trim() === '') {
        allAddedQuestionsHaveContent = false;
        break;
      }
    }
    
    return {
      hasAtLeastOneQuestion,
      allAddedQuestionsHaveContent,
      isValid: hasAtLeastOneQuestion && allAddedQuestionsHaveContent
    };
  }, [questions, numberOfQuestions]);

  // Update form data when questions change
  useEffect(() => {
    const validation = validateQuestions();
    updateFormData({
      ...questions,
      questions_validation: validation
    });
  }, [questions, numberOfQuestions]); // Remove updateFormData from dependencies to prevent infinite loop

  const handleQuestionChange = useCallback((questionId, value) => {
    setQuestions(prev => ({
      ...prev,
      [questionId]: value
    }));
  }, []);

  const deleteQuestion = useCallback((questionIndex) => {
    let newQuestionCount = 1;
    
    setQuestions(prev => {
      const newQuestions = {};
      let newIndex = 1;
      
      // Reorder questions, skipping the deleted one
      Object.keys(prev).forEach(key => {
        const currentIndex = parseInt(key.split('_')[1]);
        if (currentIndex !== questionIndex && prev[key].trim() !== '') {
          newQuestions[`question_${newIndex}`] = prev[key];
          newIndex++;
        }
      });
      
      // Fill empty slots
      for (let i = newIndex; i <= 5; i++) {
        newQuestions[`question_${i}`] = '';
      }
      
      newQuestionCount = Math.max(newIndex, 2);
      return newQuestions;
    });
    
    // Update number of questions based on new state
    setNumberOfQuestions(newQuestionCount);
  }, []);

  const addNewQuestion = useCallback(() => {
    if (numberOfQuestions <= 5) {
      const newQuestionIndex = numberOfQuestions;
      const newQuestionId = `question_${newQuestionIndex}`;
      
      // Add the new question to the state as an empty string
      setQuestions(prev => ({
        ...prev,
        [newQuestionId]: ''
      }));
      
      setNumberOfQuestions(numberOfQuestions + 1);
    }
  }, [numberOfQuestions]);

  const questionsAreFull = () => {
    for (let index = 1; index < numberOfQuestions; index++) {
      if (!questions[`question_${index}`]?.trim()) {
        return false;
      }
    }
    return true;
  };

  const questionWord = company?.stylesheet_name === 'rjpe' ? 'discovery' : 'knockout';

  return (
    <div className="space-y-8 bg-succession-bg-primary text-succession-text-primary border border-succession-border rounded-2xl p-8 shadow-succession-md transition-theme duration-theme">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-succession-text-primary">
          <FormattedMessage 
            id="succession_deals.wizard.steps.deal_questions.title" 
            values={{ questionWord: questionWord.charAt(0).toUpperCase() + questionWord.slice(1) }}
          />
        </h2>
        <p className="mt-2 text-lg text-succession-text-secondary">
          <FormattedMessage 
            id="succession_deals.wizard.steps.deal_questions.description" 
            values={{ questionWord }}
          />
        </p>
        {company?.stylesheet_name === 'rjpe' && (
          <div className="mt-4 p-3 bg-succession-bg-secondary border border-succession-border rounded-md">
            <p className="text-sm text-succession-text-primary">
              <FormattedMessage 
                id="succession_deals.wizard.steps.deal_questions.help_text_before_link"
                values={{ questionWord }}
              />
              {' '}
              <a
                href="http://help-practiceexchange.rjf.com/en/articles/5152018-learn-about-discovery-questions-in-the-meeting-room"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:opacity-70 text-succession-primary"
              >
                <FormattedMessage id="succession_deals.wizard.steps.deal_questions.help_article_link" />
              </a>
              {' '}
              <FormattedMessage id="succession_deals.wizard.steps.deal_questions.help_text_after_link" />
            </p>
          </div>
        )}
      </div>

      <div className="space-y-6">
        {/* Question Inputs */}
        {Array.from({ length: numberOfQuestions - 1 }, (_, i) => i + 1).map((index) => {
          const questionId = `question_${index}`;
          const questionValue = questions[questionId];
          
          return (
            <QuestionInput
              key={questionId}
              questionId={questionId}
              index={index}
              value={questionValue || ''}
              onDelete={deleteQuestion}
              onChange={handleQuestionChange}
              questionWord={questionWord}
              stylesheetName={company?.stylesheet_name}
            />
          );
        })}

        {/* Add Question Button */}
        <div className="text-center">
          <Button
            type="button"
            onClick={addNewQuestion}
            disabled={numberOfQuestions >= 6 || !questionsAreFull()}
            variant="primary"
            size="lg"
          >
            <FormattedMessage id="succession_deals.wizard.steps.deal_questions.add_question_button" />
          </Button>
          {numberOfQuestions >= 6 && (
            <p className="mt-2 text-sm text-succession-text-muted">
              <FormattedMessage id="succession_deals.wizard.steps.deal_questions.maximum_questions_error" />
            </p>
          )}
          {!questionsAreFull() && numberOfQuestions < 6 && (
            <p className="mt-2 text-sm text-succession-text-muted">
              <FormattedMessage id="succession_deals.wizard.steps.deal_questions.complete_current_questions_error" />
            </p>
          )}
        </div>
      </div>

      {/* Information Section */}
      <div className="rounded-md bg-succession-bg-secondary border border-succession-border p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-succession-text-muted" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-succession-text-primary">
              <FormattedMessage 
                id="succession_deals.wizard.steps.deal_questions.info_section_title" 
                values={{ questionWord }}
              />
            </h3>
            <div className="mt-2 text-sm text-succession-text-secondary">
              <ul className="list-disc pl-5 space-y-1">
                <li><FormattedMessage id="succession_deals.wizard.steps.deal_questions.info_point_1" /></li>
                <li><FormattedMessage id="succession_deals.wizard.steps.deal_questions.info_point_2" /></li>
                <li><FormattedMessage id="succession_deals.wizard.steps.deal_questions.info_point_3" /></li>
                <li><FormattedMessage id="succession_deals.wizard.steps.deal_questions.info_point_4" /></li>
                <li><FormattedMessage id="succession_deals.wizard.steps.deal_questions.info_point_5" /></li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Validation Summary */}
      {(() => {
        const validation = validateQuestions();
        return validation.isValid ? (
          <div className="bg-succession-bg-secondary border-2 border-succession-border rounded-xl p-6 shadow-succession-md">
            <div className="text-center">
              <h3 className="text-lg font-bold text-succession-text-primary mb-2">
                <FormattedMessage id="succession_deals.wizard.steps.deal_questions.validation_success_title" />
              </h3>
              <p className="text-succession-text-secondary">
                <FormattedMessage 
                  id="succession_deals.wizard.steps.deal_questions.validation_success_description" 
                  values={{ questionWord }}
                />
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-succession-bg-secondary border-2 border-succession-border rounded-xl p-6 shadow-succession-md">
            <div className="text-center">
              <h3 className="text-lg font-bold text-succession-text-primary mb-2">
                <FormattedMessage id="succession_deals.wizard.steps.deal_questions.validation_error_title" />
              </h3>
              <p className="text-succession-text-secondary mb-4">
                <FormattedMessage id="succession_deals.wizard.steps.deal_questions.validation_error_description" />
              </p>
              
              <div className="text-left max-w-md mx-auto space-y-2">
                {!validation.hasAtLeastOneQuestion && (
                  <div className="flex items-center space-x-2 text-succession-text-secondary">
                    <span className="text-sm">
                      <FormattedMessage 
                        id="succession_deals.wizard.steps.deal_questions.validation_add_one_question" 
                        values={{ questionWord }}
                      />
                    </span>
                  </div>
                )}
                {validation.hasAtLeastOneQuestion && !validation.allAddedQuestionsHaveContent && (
                  <div className="flex items-center space-x-2 text-succession-text-secondary">
                    <span className="text-sm">
                      <FormattedMessage id="succession_deals.wizard.steps.deal_questions.validation_complete_all_questions" />
                    </span>
                  </div>
                )}
                {validation.hasAtLeastOneQuestion && validation.allAddedQuestionsHaveContent && (
                  <div className="flex items-center space-x-2 text-succession-text-secondary">
                    <span className="text-sm">
                      <FormattedMessage id="succession_deals.wizard.steps.deal_questions.validation_all_complete" />
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

DealQuestions.propTypes = {
  formData: PropTypes.object.isRequired,
  updateFormData: PropTypes.func.isRequired,
  errors: PropTypes.object,
  company: PropTypes.object
};

export default DealQuestions; 